/**
 * SQL 위험도 판정 — 실행 전 확인창(Phase 2)의 근거.
 *
 * 주의: 여기서 만든 문자열은 **판정 전용**이다. 실제 실행에는 원본 SQL 을 보낸다.
 * (주석은 SQL 의 일부라 지우면 안 된다 — sanitizeQuery 참고)
 */

/**
 * 판정용 정규화 — 주석과 문자열 리터럴을 걷어낸다.
 * 리터럴을 지우지 않으면 `SET memo='delete all'` 같은 값이 키워드로 오인된다.
 */
function normalizeForAnalysis(sql) {
  if (!sql || typeof sql !== 'string') return '';
  return sql
    .replace(/\/\*[\s\S]*?\*\//g, ' ')   // 블록 주석
    .replace(/--[^\n]*/g, ' ')            // 라인 주석
    .replace(/'(?:[^']|'')*'/g, "''")     // 작은따옴표 리터럴 ('' 이스케이프 포함)
    .replace(/\s+/g, ' ')
    .trim();
}

const DML_VERBS = ['INSERT', 'UPDATE', 'DELETE', 'MERGE'];
const DDL_VERBS = ['CREATE', 'ALTER', 'DROP', 'TRUNCATE', 'RENAME', 'COMMENT', 'GRANT', 'REVOKE'];

/**
 * 문장 1개 분석.
 * @returns {{kind:'SELECT'|'DML'|'DDL'|'OTHER', verb:string, missingWhere:boolean}}
 */
export function analyzeStatement(sql) {
  const s = normalizeForAnalysis(sql);
  if (!s) return { kind: 'OTHER', verb: '', missingWhere: false };

  const upper = s.toUpperCase();
  const verb = (upper.match(/^[A-Z]+/) || [''])[0];

  let kind = 'OTHER';
  if (verb === 'SELECT' || verb === 'WITH') kind = 'SELECT';
  else if (DML_VERBS.includes(verb)) kind = 'DML';
  else if (DDL_VERBS.includes(verb)) kind = 'DDL';

  // UPDATE / DELETE 에 WHERE 가 없으면 전체 행이 대상 — 가장 흔한 사고
  let missingWhere = false;
  if (verb === 'UPDATE' || verb === 'DELETE') {
    missingWhere = !/\bWHERE\b/.test(upper);
  }

  return { kind, verb, missingWhere };
}

/**
 * 여러 문장을 한 번에 요약 (전체 실행용).
 * @param {string[]} statements
 */
export function analyzeStatements(statements) {
  const list = (statements || []).map(analyzeStatement);
  return {
    hasDml: list.some(a => a.kind === 'DML'),
    hasDdl: list.some(a => a.kind === 'DDL'),
    missingWhereVerbs: list.filter(a => a.missingWhere).map(a => a.verb),
    items: list,
  };
}

/**
 * 확인창에 쓸 라벨 — 모듈 스코프라 t() 를 못 쓰므로 키만 담는다.
 * 현재 참조하는 곳이 없다(App.js 는 common:preRun.* 를 직접 쓴다). 쓰게 되면
 * `t(KIND_LABEL_KEY[kind])` 형태로 호출할 것.
 */
export const KIND_LABEL_KEY = {
  SELECT: 'settings:sqlKind.select',
  DML: 'settings:sqlKind.dml',
  DDL: 'settings:sqlKind.ddl',
  OTHER: 'settings:sqlKind.other',
};
